import React, { useState } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { X, Pencil, CalendarIcon } from 'lucide-react';

/**
 * EditBookingModal — collects optional date/time/location (+ optional message)
 * changes for a trial booking and forwards them to the caller.
 *
 * Props:
 *   booking            — the booking object (for displaying current details).
 *   showMessageField   — whether to render the optional message textarea.
 *   submitting         — boolean parent-controlled flag for the submit button.
 *   onClose()
 *   onSubmit({ date?, time?, location? }, message?)
 */
const EditBookingModal = ({ booking, showMessageField = false, submitting = false, onClose, onSubmit }) => {
    const [date, setDate] = useState(null); // Date object, or null
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [time, setTime] = useState('');
    const [location, setLocation] = useState('');
    const [message, setMessage] = useState('');

    const handleSubmit = () => {
        const changes = {};
        // Submitted as ISO yyyy-mm-dd — display is UK format (dd MMM yyyy), value is unchanged
        if (date) changes.date = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        if (time.trim()) changes.time = time.trim();
        if (location.trim()) changes.location = location.trim();
        onSubmit(changes, message.trim());
    };

    const hasChange = !!(date || time.trim() || location.trim());

    return (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => !submitting && onClose()}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-booking-modal">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <Pencil className="h-5 w-5 text-[#7C3AED]" />
                        </div>
                        <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Request edit</h3>
                    </div>
                    <button onClick={() => !submitting && onClose()} className="text-slate-400 hover:text-slate-600" data-testid="edit-modal-close">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                {booking && (
                    <p className="text-sm text-slate-500 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                        {booking.candidate_name || booking.company_name} &middot; {booking.job_title}
                        {booking.start_date && (
                            <span className="block text-xs text-slate-400 mt-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                Currently starts {booking.start_date}{booking.start_time ? ` at ${booking.start_time} (UK time)` : ''}{booking.location ? ` · ${booking.location}` : ''}
                            </span>
                        )}
                    </p>
                )}
                <p className="text-xs text-slate-500 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Leave blank to keep current values. Fill any field you want to change.
                </p>
                <div className="space-y-3 mb-4">
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block" style={{ fontFamily: 'Unbounded, sans-serif' }}>New date (UK format)</label>
                        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between rounded-xl border border-purple-200 px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                                    data-testid="edit-date-input"
                                >
                                    <span className={date ? 'text-slate-900' : 'text-slate-400'}>
                                        {date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'dd/mm/yyyy'}
                                    </span>
                                    <CalendarIcon className="h-4 w-4 text-slate-400" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-white z-[70]" align="start">
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={(d) => { setDate(d || null); setDatePickerOpen(false); }}
                                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                    initialFocus
                                    data-testid="edit-date-calendar"
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block" style={{ fontFamily: 'Unbounded, sans-serif' }}>New time (UK time)</label>
                        <input
                            type="text"
                            placeholder="e.g. 10:00 AM"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            className="w-full rounded-xl border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                            data-testid="edit-time-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block" style={{ fontFamily: 'Unbounded, sans-serif' }}>New location</label>
                        <input
                            type="text"
                            placeholder="e.g. 123 Old Street, London"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="w-full rounded-xl border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                            data-testid="edit-location-input"
                        />
                    </div>
                    {showMessageField && (
                        <div>
                            <label className="text-xs text-slate-500 mb-1 block" style={{ fontFamily: 'Unbounded, sans-serif' }}>Message (optional)</label>
                            <textarea
                                rows={3}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Add context for the employer"
                                className="w-full rounded-xl border border-purple-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                                data-testid="edit-message-input"
                            />
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} className="rounded-full" disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={submitting || !hasChange}
                        className="rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#7C3AED] text-white hover:opacity-90"
                        data-testid="edit-submit-btn"
                    >
                        {submitting ? 'Sending…' : 'Send request'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EditBookingModal;
